import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * Origins a share can arrive through; one share may carry several at once.
 * Derived best-effort from the projection tables (folder_share_id is the
 * only persisted origin marker; rule-projected and manual rows are
 * indistinguishable) — PIMAO-874's grant store makes it exact behind this
 * same shape.
 */
export const ShareProvenance = S.Union(S.Struct({ kind: S.Literal("personal") }), S.Struct({ kind: S.Literal("group"), groupId: S.optional(S.UUID) }), S.Struct({ kind: S.Literal("type-rule"), companyType: S.String }), S.Struct({ kind: S.Literal("folder"), folderShareId: S.UUID }));
/**
 * One row of everything shared with a user. View-shaped on vw_user_shares
 * (courses + files only, exactly like the view) with snake_case field names
 * kept 1:1 so surface migrations and parity diffs stay mechanical. `id` is
 * the SHARE row id, never the file/course id. Timestamps are ISO-8601
 * strings. `provenance` is not a legacy column — it's composed server-side
 * from the projection tables, so it uses the contract's native camelCase
 * rather than the snake_case parity rule above.
 */
export const UserShare = S.Struct({
    id: S.UUID,
    user_id: S.NullOr(S.UUID),
    client_id: S.UUID,
    shared_by: S.NullOr(S.UUID),
    shared_by_name: S.String,
    share_date: S.String,
    share_type: S.Literal("online_course", "file"),
    online_course_id: S.NullOr(S.UUID),
    online_course_title: S.NullOr(S.String),
    online_course_language: S.NullOr(LanguageSchema),
    online_course_translation_group_id: S.NullOr(S.Int),
    file_id: S.NullOr(S.UUID),
    filename_download: S.NullOr(S.String),
    file_language: S.NullOr(LanguageSchema),
    file_translation_group_id: S.NullOr(S.Int),
    completed: S.NullOr(S.Boolean),
    completed_at: S.NullOr(S.String),
    provenance: S.Array(ShareProvenance),
});
export const Forbidden = S.Struct({ _tag: S.Literal("Forbidden") });
export const SharesForUserParams = S.Struct({
    user_id: S.UUID,
    /** When set, keeps only shares whose file OR course language matches. */
    language: S.optional(LanguageSchema),
    limit: S.optionalWith(S.NumberFromString.pipe(S.int(), S.between(1, 100)), {
        default: () => 20,
    }),
    offset: S.optionalWith(S.NumberFromString.pipe(S.int(), S.greaterThanOrEqualTo(0)), { default: () => 0 }),
});
/**
 * Everything shared with one user (admin employee-activity panel intent).
 * Ordered by share_date DESC, id ASC; offset pagination per decision 8.
 */
export const SharesForUser = HttpApiEndpoint.get("sharesForUser", "/shares/for-user")
    .setUrlParams(SharesForUserParams)
    .addSuccess(S.Struct({ items: S.Array(UserShare), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const sharesGroup = HttpApiGroup.make("shares")
    .add(SharesForUser)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=shares.js.map